"use client";
import CategoryPageLayout from "components/General/CategoryElements/CategoryPageLayout/CategoryPageLayout";
import PageLayout from "components/PageLayout/PageLayout";
import { useMemo, useState } from "react";

const CategoryPage = ({
  page,
  products,
  categories,
  product_properties_lookup,
  filters,
}) => {
  const [selectedFilters, setSelectedFilters] = useState({});

  /**
   * Get the product ids that match the selected filters
   * @param {Object} _selectedFilters - The selected filters in the format { filter_slug: [value1, value2, ...], ... }
   * @returns {Array} - Array of product IDs that match the selected filters
   */
  function getProductsIdsForFilters(_selectedFilters) {
    const selectedFiltersArray = Object.entries(_selectedFilters).filter(
      ([key, values]) => values.length > 0
    );

    //If no filters are selected, all products are valid
    if (!products.length || selectedFiltersArray.length == 0) {
      return products.map((p) => p.ID);
    }
    //We will use sets to calculate the intersection of products for each filter
    let selected_product_ids = null;
    for (const [filter_key, filter_values] of selectedFiltersArray) {
      //Get all product ids that are valid for the current filter.
      const products_ids_for_filter = [];
      for (const filter_value of filter_values) {
        const product_ids =
          product_properties_lookup?.[filter_key]?.[filter_value];

        products_ids_for_filter.push(...(product_ids || []));
      }

      //Create a set for the current filter
      const product_category_set = new Set(products_ids_for_filter);

      //If this is the first filter, set the selected_product_ids to the current set
      if (!selected_product_ids) {
        selected_product_ids = product_category_set;
      } else {
        //Calculate the intersection of the current set with the selected_product_ids
        // This will ensure that only products that are valid for all selected filters are kept
        selected_product_ids =
          product_category_set.intersection(selected_product_ids);
      }
    }

    return [...selected_product_ids.values()];
  }

  /**
   * Get the products that match the selected filters
   * */
  const filteredProducts = useMemo(() => {
    const selected_product_ids = getProductsIdsForFilters(selectedFilters);
    return products.filter((p) => selected_product_ids.includes(p.ID));
  }, [selectedFilters, product_properties_lookup, products]);

  /**
   * Get the filters that have options available in the filtered products
   */
  const filteredFilters = useMemo(() => {
    //if no filters are selected, return all filters
    // if (Object.keys(selectedFilters).length === 0) {
    //   return filters;
    // }
    let used_filters = [];

    //Loop over all filters and check if they have options available in the filtered products
    for (const filter of filters) {
      const { slug, terms = [] } = filter;
      const _ref_name = slug;
      //If the filter is not in the product properties lookup, there is no product with this filter
      if (!product_properties_lookup?.[_ref_name]) {
        continue;
      }

      // We need to check if this filter has any options available in the filtered products
      // We should show all options that are selected, even if they have no products available

      const selectedFiltersWithoutCurrent = { ...selectedFilters };
      delete selectedFiltersWithoutCurrent[_ref_name];

      const validProductIdsWithoutCurrentFilter = getProductsIdsForFilters(
        selectedFiltersWithoutCurrent
      );

      const valid_terms = terms.filter((term) => {
        const term_name = term?.name;

        //If filter term is selected, always show the term
        if (selectedFilters[_ref_name]?.includes(term_name)) {
          return true;
        }

        //If the filter has no products, skip it
        const products_ids_having_term =
          product_properties_lookup[_ref_name]?.[term_name];

        if (!products_ids_having_term) {
          return false;
        }

        //check if products_with_term has any of the product_ids
        const has_product = products_ids_having_term.some((id) =>
          validProductIdsWithoutCurrentFilter.includes(id)
        );

        return has_product;
      });

      //We should skip filters that have no valid terms
      if (!valid_terms.length) {
        continue;
      }
      //Add the filter with the valid terms to the used_filters
      used_filters.push({ ...filter, terms: valid_terms });
    }
    return used_filters;
  }, [filters, filteredProducts, selectedFilters]);

  const currentCategoriesSlugsArr = findCatInChildren(categories, page?.cat_id);

  const breadcrumbSlugs = (currentCategoriesSlugsArr || []).map(
    (cat, index) => ({
      name: cat?.name,
      slug:
        index == currentCategoriesSlugsArr.length - 1 ? null : cat?.permalink,
    })
  );

  return (
    <>
      <PageLayout breadcrumbs={breadcrumbSlugs}>
        <CategoryPageLayout
          title={page?.title || ""}
          description={page?.content || ""}
          active_cat={page?.cat_id}
          categories={categories}
          products={filteredProducts}
          filters={filteredFilters}
          selectedFilters={selectedFilters}
          setSelectedFilters={setSelectedFilters}
        />
      </PageLayout>
    </>
  );
};

export default CategoryPage;

function findCatInChildren(arr, cat_id) {
  for (const cat of arr) {
    if (cat?.term_id == cat_id) {
      return [cat];
    }
    if (cat?.children?.length > 0) {
      const found = findCatInChildren(cat?.children, cat_id);
      if (found) {
        return [cat, ...found];
      }
    }
  }
  return false;
}
